import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { useAuth } from '../hooks/useAuth.js'
import { useUserDevices } from '../hooks/useUserDevices.js'
import { useDeviceStatus } from '../hooks/useDeviceStatus.js'
import { useDeviceHealthMonitor } from '../hooks/useDeviceHealthMonitor.js'
import { useDeviceAccessTracking } from '../hooks/useDeviceAccessTracking.js'

// Cloud context state shape - streamlined for minimal re-renders
const initialState = {
  // Authentication state
  user: null,
  isAuthenticated: false,
  authLoading: true,
  
  // Device management
  devices: [],
  devicesLoading: true,
  selectedDeviceId: null,
  selectedDevice: null,
  deviceStatus: null,
  statusLoading: true,
  
  // UI state
  isFleetView: false,
  navigationContext: null, // 'notification-access-request' or null
  
  // Connection state
  connectionStatus: 'disconnected',
  lastSeen: null,
  
  // Internal optimization flags
  _isInitialized: false,
  _isRestoringSelection: false,
}

// Action types - ES2022+ constants for better tree-shaking
export const ActionTypes = Object.freeze({
  SET_AUTH_STATE: 'SET_AUTH_STATE',
  SET_DEVICES: 'SET_DEVICES',
  SET_SELECTED_DEVICE: 'SET_SELECTED_DEVICE',
  SET_DEVICE_STATUS: 'SET_DEVICE_STATUS',
  SET_FLEET_VIEW: 'SET_FLEET_VIEW',
  SET_NAVIGATION_CONTEXT: 'SET_NAVIGATION_CONTEXT',
  SET_INITIALIZED: 'SET_INITIALIZED',
  SET_RESTORING_SELECTION: 'SET_RESTORING_SELECTION',
})

// Reducer for cloud state management - optimized for React 19+
const cloudReducer = (state, action) => {
  // Handle functional dispatch pattern
  if (typeof action === 'function') {
    return action(state)
  }
  
  switch (action.type) {
    case ActionTypes.SET_AUTH_STATE:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: !!action.payload.user,
        authLoading: action.payload.loading,
      }
    
    case ActionTypes.SET_DEVICES:
      return {
        ...state,
        devices: action.payload.devices,
        devicesLoading: action.payload.loading,
        _isInitialized: true,
      }
    
    case ActionTypes.SET_SELECTED_DEVICE:
      return {
        ...state,
        selectedDeviceId: action.payload.deviceId,
        selectedDevice: action.payload.device,
        navigationContext: action.payload.navigationContext ?? null,
      }
    
    case ActionTypes.SET_DEVICE_STATUS:
      return {
        ...state,
        deviceStatus: action.payload.status,
        statusLoading: action.payload.loading,
        connectionStatus: action.payload.status?.connectionStatus ?? 'disconnected',
        lastSeen: action.payload.status?.lastSeen ?? null,
      }
    
    case ActionTypes.SET_FLEET_VIEW:
      return { ...state, isFleetView: action.payload }
    
    case ActionTypes.SET_NAVIGATION_CONTEXT:
      return { ...state, navigationContext: action.payload }
    
    case ActionTypes.SET_RESTORING_SELECTION:
      return { ...state, _isRestoringSelection: action.payload }
    
    default:
      return state
  }
}

// Create context
const CloudContext = createContext()

// Local storage key constant
const SELECTED_DEVICE_KEY = 'revatek-connect-selected-device'

/**
 * Simple global polling control using Page Visibility API + inactivity timer
 * Avoids infinite render loops by using stable boolean state
 */
const useGlobalPollingControl = () => {
  const [isPollingPaused, setIsPollingPaused] = useState(false)
  
  useEffect(() => {
    let inactivityTimer
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsPollingPaused(true)
      } else {
        setIsPollingPaused(false)
        resetInactivityTimer()
      }
    }
    
    const handleUserActivity = () => {
      setIsPollingPaused(false)
      resetInactivityTimer()
    }
    
    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer)
      inactivityTimer = setTimeout(() => {
        setIsPollingPaused(true)
      }, 10 * 60 * 1000) // 10 minutes of inactivity
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    document.addEventListener('mousedown', handleUserActivity)
    document.addEventListener('keydown', handleUserActivity)
    document.addEventListener('mousemove', handleUserActivity)
    
    resetInactivityTimer() // Start timer
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      document.removeEventListener('mousedown', handleUserActivity)
      document.removeEventListener('keydown', handleUserActivity)
      document.removeEventListener('mousemove', handleUserActivity)
      clearTimeout(inactivityTimer)
    }
  }, [])
  
  return isPollingPaused
}

/**
 * Cloud Provider component that wraps the app with cloud functionality
 * Firebase v11+ implementation with React 19+ optimizations
 */
export function CloudProvider({ children }) {
  const [state, dispatch] = useReducer(cloudReducer, initialState)
  
  // Simple global polling control - stable, no re-render loops
  const globalPollingPaused = useGlobalPollingControl()
  
  // Firebase hooks
  const authHook = useAuth()
  const { devices, loading: devicesLoading } = useUserDevices()
  const { status: deviceStatus, loading: statusLoading } = useDeviceStatus(state.selectedDeviceId)
  
  // Device monitoring hooks
  useDeviceHealthMonitor(globalPollingPaused)
  useDeviceAccessTracking(state.selectedDeviceId, !!state.selectedDeviceId)
  
  // Stable auth methods reference - prevents unnecessary re-renders
  const authMethods = useMemo(() => ({
    login: authHook.login,
    register: authHook.register,
    loginWithGoogle: authHook.loginWithGoogle,
    resetPassword: authHook.resetPassword,
    changeEmail: authHook.changeEmail,
    changePassword: authHook.changePassword,
    deleteAccount: authHook.deleteAccount,
    sendVerificationEmail: authHook.sendVerificationEmail,
    resendVerificationEmail: authHook.resendVerificationEmail,
    refreshUser: authHook.refreshUser,
    updateUserProfile: authHook.updateUserProfile,
    logout: authHook.logout,
  }), [
    authHook.login,
    authHook.register,
    authHook.loginWithGoogle,
    authHook.resetPassword,
    authHook.changeEmail,
    authHook.changePassword,
    authHook.deleteAccount,
    authHook.sendVerificationEmail,
    authHook.resendVerificationEmail,
    authHook.refreshUser,
    authHook.updateUserProfile,
    authHook.logout,
  ])
  
  // Memoized device selection handler - prevents stale state
  const selectDevice = useCallback((deviceId, navigationContext = null) => {
    dispatch(currentState => {
      const device = currentState.devices.find(d => d.deviceId === deviceId) ?? null
      
      // Prevent unnecessary updates
      if (currentState.selectedDeviceId === deviceId && !navigationContext) {
        return currentState
      }
      
      // Persist selection
      try {
        localStorage.setItem(SELECTED_DEVICE_KEY, deviceId)
      } catch (error) {
        if (import.meta.env.DEV) console.warn('Failed to persist device selection:', error)
      }
      
      return {
        ...currentState,
        selectedDeviceId: deviceId,
        selectedDevice: device,
        navigationContext,
      }
    })
  }, [])
  
  // Clear navigation context
  const clearNavigationContext = useCallback(() => {
    dispatch({ type: ActionTypes.SET_NAVIGATION_CONTEXT, payload: null })
  }, [])
  
  // Fleet view toggle
  const setFleetView = useCallback((isFleetView) => {
    dispatch({ type: ActionTypes.SET_FLEET_VIEW, payload: isFleetView })
  }, [])
  
  // Update auth state
  useEffect(() => {
    dispatch({
      type: ActionTypes.SET_AUTH_STATE,
      payload: { user: authHook.user, loading: authHook.loading },
    })
  }, [authHook.user, authHook.loading])
  
  // Update devices state
  useEffect(() => {
    dispatch({
      type: ActionTypes.SET_DEVICES,
      payload: { devices, loading: devicesLoading },
    })
  }, [devices, devicesLoading])
  
  // Update device status
  useEffect(() => {
    dispatch({
      type: ActionTypes.SET_DEVICE_STATUS,
      payload: { status: deviceStatus, loading: statusLoading },
    })
  }, [deviceStatus, statusLoading])
  
  // Auto-select first device after initialization
  useEffect(() => {
    if (!state._isInitialized || 
        state._isRestoringSelection || 
        state.selectedDeviceId || 
        state.devices.length === 0 || 
        state.devicesLoading) {
      return
    }
    
    const timeoutId = setTimeout(() => {
      selectDevice(state.devices[0].deviceId)
    }, 10)
    
    return () => clearTimeout(timeoutId)
  }, [state._isInitialized, state._isRestoringSelection, state.selectedDeviceId, state.devices, state.devicesLoading, selectDevice])
  
  // Handle transition from no devices to having devices
  useEffect(() => {
    if (state._isInitialized && 
        !state.devicesLoading && 
        !state.selectedDeviceId && 
        state.devices.length > 0) {
      
      const timeoutId = setTimeout(() => {
        selectDevice(state.devices[0].deviceId)
      }, 100)
      
      return () => clearTimeout(timeoutId)
    }
  }, [state._isInitialized, state.devicesLoading, state.selectedDeviceId, state.devices, selectDevice])
  
  // Update device status - streamlined for performance with error handling
  useEffect(() => {
    try {
      dispatch({
        type: ActionTypes.SET_DEVICE_STATUS,
        payload: { status: deviceStatus, loading: statusLoading },
      })
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Device status update failed:', error)
      }
    }
  }, [deviceStatus, statusLoading])
  
  // Restore device selection on mount
  useEffect(() => {
    if (!state._isInitialized || state.selectedDeviceId || state.devices.length === 0) {
      return
    }
    
    dispatch({ type: ActionTypes.SET_RESTORING_SELECTION, payload: true })
    
    try {
      const savedDeviceId = localStorage.getItem(SELECTED_DEVICE_KEY)
      if (savedDeviceId && state.devices.some(d => d.deviceId === savedDeviceId)) {
        selectDevice(savedDeviceId)
      }
    } catch (error) {
      if (import.meta.env.DEV) console.warn('Failed to restore device selection:', error)
    }
    
    const timeoutId = setTimeout(() => {
      dispatch({ type: ActionTypes.SET_RESTORING_SELECTION, payload: false })
    }, 50)
    
    return () => clearTimeout(timeoutId)
  }, [state._isInitialized, state.selectedDeviceId, state.devices, selectDevice])
  
  // Keep selectedDevice synchronized with devices array for real-time updates
  useEffect(() => {
    if (state.selectedDeviceId && state.devices.length > 0) {
      const currentDevice = state.devices.find(d => d.deviceId === state.selectedDeviceId)
      
      // Only update if device data changed to prevent unnecessary re-renders
      if (currentDevice && 
          (currentDevice.nickname !== state.selectedDevice?.nickname ||
           currentDevice.location !== state.selectedDevice?.location ||
           currentDevice.lastModified !== state.selectedDevice?.lastModified)) {
        
        dispatch({
          type: ActionTypes.SET_SELECTED_DEVICE,
          payload: {
            deviceId: state.selectedDeviceId,
            device: currentDevice,
            navigationContext: state.navigationContext,
          },
        })
      }
    }
  }, [state.devices, state.selectedDeviceId, state.selectedDevice, state.navigationContext])

  // Memoized context value - prevents unnecessary provider re-renders
  const value = useMemo(() => {
    // Filter out internal flags from public state
    const { _isInitialized, _isRestoringSelection, ...publicState } = state
    
    return {
      ...publicState,
      ...authMethods,
      selectDevice,
      setFleetView,
      clearNavigationContext,
      globalPollingPaused, // Simple boolean - stable reference
      isDevelopmentMode: import.meta.env.DEV,
      hasDevices: state.devices.length > 0,
      hasMultipleDevices: state.devices.length > 1,
      isOnline: state.connectionStatus === 'online',
      currentDeviceName: (() => {
        const currentDevice = state.devices.find(d => d.deviceId === state.selectedDeviceId)
        return currentDevice?.nickname ?? 
               currentDevice?.deviceId ?? 
               state.selectedDevice?.nickname ?? 
               state.selectedDevice?.deviceId ?? 
               'No Device Selected'
      })(),
    }
  }, [state, authMethods, selectDevice, setFleetView, clearNavigationContext, globalPollingPaused])
  
  return (
    <CloudContext.Provider value={value}>
      {children}
    </CloudContext.Provider>
  )
}

/**
 * Hook to access cloud context - React 19+ optimized
 * @returns {Object} Cloud context value
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useCloudContext() {
  const context = useContext(CloudContext)
  if (!context) {
    throw new Error('useCloudContext must be used within a CloudProvider')
  }
  return context
}

export default CloudContext
